import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Modal from "react-bootstrap/Modal";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { ResumeCitizenAttendancesIndicatorB2Data } from "@/services/esus/indicator/esb/b2/getResumeCitizensAttendancesIndicatorB2";
import { Paginated } from "@/@types/generics/paginated";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorEsb } from "@/utils/indicatorsEsb";
import {
	DetailedCitizenAttendancesIndicatorB2,
	getDetailedCitizenAttendancesIndicatorB2,
} from "@/services/esus/indicator/esb/b2/getDetailedCitizenAttendancesIndicatorB2";

import { IndicatorEsbB2AttendancesFilters } from "./IndicatorEsbB2AttendancesFilters";
import { IndicatorEsbB2AttendancesTable } from "./IndicatorEsbB2AttendancesTable";
import { IndicatorEsbB2CitizenHistory } from "./IndicatorEsbB2CitizenHistory";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsbB2AttendancesProps = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	search: string;
	setSearch: (search: string) => void;
	isEvaluated: boolean | undefined;
	setIsEvaluated: (isEvaluated: boolean | undefined) => void;
	isLoading: boolean;
	paginatedAttendances: Paginated<ResumeCitizenAttendancesIndicatorB2Data>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeCitizensAttendancesIndicatorB2: () => Promise<
		ResumeCitizenAttendancesIndicatorB2Data[]
	>;
};

export function IndicatorEsbB2Attendances({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	search,
	setSearch,
	isEvaluated,
	setIsEvaluated,
	isLoading,
	paginatedAttendances,
	pageNumber,
	setPageNumber,
	handleGetAllResumeCitizensAttendancesIndicatorB2,
}: Readonly<IndicatorEsbB2AttendancesProps>) {
	const { uf, ibgeCode } = useCurrentAccount();
	const [isCitizenHistoryModalOpen, setIsCitizenHistoryModalOpen] = useState(false);
	const [citizenWithDentalAttendances, setCitizenWithDentalAttendances] =
		useState<DetailedCitizenAttendancesIndicatorB2>(
			new DetailedCitizenAttendancesIndicatorB2()
		);
	const [isLoadingCitizenWithDentalAttendances, setIsLoadingCitizenWithDentalAttendances] =
		useState(false);
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);

	async function handleGetCitizenWithAttendances(citizenId: number, ine?: string) {
		setIsLoadingCitizenWithDentalAttendances(true);
		setCitizenWithDentalAttendances(
			await getDetailedCitizenAttendancesIndicatorB2({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne || ine,
				citizenId,
			})
		);
		setIsLoadingCitizenWithDentalAttendances(false);
		setIsCitizenHistoryModalOpen(true);
	}

	async function handleExportTable() {
		setIsLoadingExportData(true);
		const attendances = await handleGetAllResumeCitizensAttendancesIndicatorB2();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(attendances).length > 0) {
			const excelData: GenericObject[] =
				transformIndicatorB2AttendancesToGenericObjectArray(attendances);
			exportExcel(
				excelData,
				`Atendimentos odontológicos - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorB2AttendancesToGenericObjectArray(
		indicatorB2Attendances: ResumeCitizenAttendancesIndicatorB2Data[]
	) {
		return indicatorB2Attendances.map(
			(citizenWithAttendances) =>
				({
					Nome: citizenWithAttendances.fullName,
					DN: citizenWithAttendances.birthdate
						? dayjs(citizenWithAttendances.birthdate).format("DD/MM/YYYY")
						: "",
					Idade: citizenWithAttendances.birthdate
						? getAge(citizenWithAttendances.birthdate)
						: "",
					Documento: citizenWithAttendances.cpf ?? citizenWithAttendances.cns ?? "",
					Area: citizenWithAttendances.area ?? "FA",
					"Equipe ESF Cidadão": citizenWithAttendances.healthTeamName ?? "",
					"Equipe ESF Ine Cidadão": citizenWithAttendances.healthTeamIne ?? "",
					"Equipe ESB Atendimento": citizenWithAttendances.esbHealthTeamName ?? "",
					"Equipe ESB Ine Atendimento": citizenWithAttendances.esbHealthTeamIne ?? "",
					"Equipe ESF (vínculada a ESB atendimento)":
						citizenWithAttendances.linkedHealthTeamName ?? "",
					"Equipe ESF Ine (vínculada a ESB atendimento)":
						citizenWithAttendances.linkedHealthTeamIne ?? "",
					"Última Consulta": citizenWithAttendances.treatmentStartDate
						? dayjs(citizenWithAttendances.treatmentStartDate).format("DD/MM/YYYY")
						: "",
					"Status última Consulta Odontológica Programada":
						getCitizenScoreStatusDescriptionIndicatorEsb(
							citizenWithAttendances.lastDentalAttendanceStatus
						),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen
					loading={isLoadingCitizenWithDentalAttendances || isLoadingExportData}
				/>
				<Card>
					<Card.Header>
						<IndicatorEsbB2AttendancesFilters
							search={search}
							setSearch={setSearch}
							isEvaluated={isEvaluated}
							setIsEvaluated={setIsEvaluated}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsbB2AttendancesTable
							citizensWithAttendances={paginatedAttendances.data}
							isLoading={isLoading}
							openCitizenHistory={handleGetCitizenWithAttendances}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedAttendances?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedAttendances.pageSize}
								totalPages={paginatedAttendances.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
				<Modal
					show={isCitizenHistoryModalOpen}
					onHide={() => setIsCitizenHistoryModalOpen(false)}
					size="xl"
					aria-labelledby="contained-modal-title-vcenter"
					centered
				>
					<Modal.Header closeButton>
						<Modal.Title>
							<h3 className="text-uppercase text-dark fw-bold fs-7">
								Histórico de Atendimentos Odontológicos nos últimos 12 meses
							</h3>
						</Modal.Title>
					</Modal.Header>
					<Modal.Body>
						<IndicatorEsbB2CitizenHistory
							citizenWithDentalAttendances={citizenWithDentalAttendances}
						/>
					</Modal.Body>
				</Modal>
			</Col>
		</Row>
	);
}
